import React, {useEffect, useRef, useState} from "react";
import {Card, CardBody, UncontrolledTooltip} from "reactstrap";
import FullCalendar, {
  addDays,
  createPlugin,
  EventRenderRange,
  sliceEvents
} from "@fullcalendar/react";
import {
  CalendarApi,
  DateProfile,
  Duration,
  EventClickArg,
  ViewComponentType,
  ViewProps
} from "@fullcalendar/common";
import dayGridPlugin from "@fullcalendar/daygrid";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin, {DateClickArg} from "@fullcalendar/interaction";
import EventSummaryItem from "../../../stores/domainStore/pages/models/eventSummaryItem";
import "./eventsCalendarTemplate.sass";
import EventSummaryCalendarView from "../../organisms/eventSummaryCalendarView/eventSummaryCalendarView";
import {observer} from "mobx-react-lite";
import EventDetailModal from "../../organisms/eventDetailModal/eventDetailModal";
import EventCalendarHeader from "../../organisms/eventCalendarHeader/eventCalendarHeader";
import EventDetails from "../../../stores/domainStore/pages/models/eventDetails";
import {useTranslation} from "react-i18next";
import classNames from "classnames";
import PageHeading from "../../organisms/pageHeading/pageHeading";
import {
  checkDatesEqual,
  isDateOneAfterDateTwo,
  isDateOneSameOrAfterDateTwo,
  isToday
} from "../../../utils/dateUtils";
import CalendarSchedule from "../../../stores/domainStore/pages/models/calendarSchedule";
import {IconSource} from "../../../shared/enums/iconSource";
import {addHours, intervalToDuration, parseISO} from "date-fns";
import {
  calendarViewCategory,
  calendarViewMonth,
  calendarViewSummary,
  calendarViewWeek
} from "../../../shared/constants";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import {useStores} from "../../../stores/useStores";
import {utcToZonedTime, zonedTimeToUtc} from "date-fns-tz";
import {RecurringStatus} from "../../../shared/enums/RecurringStatus";
import EventIcon from "../../molecules/eventIcon/eventIcon";
import {useLocation} from "react-router";
import {QueryParams} from "../../../shared/enums/queryParams";
import {runInAction} from "mobx";
import EventCategoryAccordion from "../../organisms/eventCategoryAccordion/eventCategoryAccordion";

export interface IEventsCalendarTemplateProps {
  events: EventSummaryItem[];
  isLoading: boolean;
  isNavigating: boolean;
  initialDate: Date;
  loadEvents: (startDate: Date, endDate: Date) => void;
  createEventOnClick: (startDate?: Date) => void;
  onEditEventClick: () => void;
  onViewEventClick: () => void;
  loadEventDetail: (eventId: string) => void;
  eventDetailIsLoading: boolean;
  eventDetail: EventDetails;
  currentCalendarView: string;
  setCurrentCalendarView: (selected: string) => void;
  calendarSchedule?: CalendarSchedule;
  isMultiVillageOptionSelected?: boolean;
  switchToManageBooking: () => void;
  switchToEditEventSeries: () => void;
  switchToViewEventSeries: () => void;
}

const defaultCalendarSlotMinTime = "08:00:00";
const defaultCalendarSlotMaxTime = "22:00:00";

const EventsCalendarTemplate: React.FC<IEventsCalendarTemplateProps> = ({
  loadEvents,
  events,
  isLoading,
  isNavigating,
  initialDate,
  createEventOnClick,
  onEditEventClick,
  onViewEventClick,
  loadEventDetail,
  eventDetailIsLoading,
  eventDetail,
  currentCalendarView,
  setCurrentCalendarView,
  calendarSchedule,
  isMultiVillageOptionSelected,
  switchToManageBooking,
  switchToEditEventSeries,
  switchToViewEventSeries
}) => {
  const {
    uiStore: {eventsUiStore},
    domainStore: {eventsDomainStore}
  } = useStores();

  const calendarRef = useRef<FullCalendar>(null);

  const [calendarApi, setCalendarApi] = useState<CalendarApi>(
    new CalendarApi()
  );
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);

  const {t} = useTranslation(["event"]);

  const [timeZone, setTimeZone] = useState(eventsUiStore.getTimeZone());

  const defaultCalendarView = eventsUiStore?.defaultCalendarView;

  const views = {
    summaryView: {
      type: "timeGrid",
      duration: {days: 2},
      dateIncrement: {days: 1},
      buttonText: "Custom",
      timeZone: timeZone
    },
    week: {
      dayHeaderFormat: {day: "numeric", weekday: "short", omitCommas: true}
    },
    dayGridMonth: {fixedWeekCount: false},
    categoryView: {duration: {months: 1}}
  };

  useEffect(() => {
    if (calendarRef.current) {
      setCalendarApi(calendarRef.current!.getApi());
    }
  });

  const handleViewChange = () => {
    switch (currentCalendarView) {
      case calendarViewMonth:
        calendarApi?.changeView(calendarViewMonth);
        break;
      case calendarViewWeek:
        calendarApi?.changeView(calendarViewWeek);
        break;
      case calendarViewSummary:
        calendarApi?.changeView(calendarViewSummary);
        break;
      case calendarViewCategory:
        calendarApi?.changeView(calendarViewCategory);
        break;
      default:
        calendarApi?.changeView(defaultCalendarView);
        break;
    }
  };

  useEffect(() => {
    const newTimeZone = eventsUiStore.getTimeZone();
    if (timeZone !== newTimeZone) {
      setTimeZone(newTimeZone);
    }
  }, [eventsUiStore, timeZone]);

  const summaryView = (
    props: ViewProps & {
      dateProfile: DateProfile;
      nextDayThreshold: Duration;
    }
  ) => {
    let segments: EventRenderRange[] = sliceEvents(props, true);

    const leftHandDate = props.dateProfile.currentRange.start;
    const rightHandDate = addDays(props.dateProfile.currentRange.start, 1);

    return (
      <EventSummaryCalendarView
        leftHandDate={leftHandDate}
        rightHandDate={rightHandDate}
        startDate={calendarSchedule?.startDate}
        endDate={calendarSchedule?.endDate}
        segments={segments}
        isMultiVillageOptionSelected={isMultiVillageOptionSelected}
        onEventClick={(event) => {
          const isRecurringEvent =
            event.recurringStatus === RecurringStatus.Series ||
            event.recurringStatus === RecurringStatus.DetachedSeries;

          const occurrenceDate = isRecurringEvent
            ? parseISO(
                event.extendedProps.eventStartDateTimeOffSet!
              ).toISOString()
            : undefined;

          eventsDomainStore.setOccurrenceDate(occurrenceDate);

          loadEventDetail(event.id);
          setIsEventDetailModalOpen(true);
        }}
        timeZone={eventsUiStore.getTimeZone()}
      />
    );
  };

  const categoryView = (
    props: ViewProps & {
      dateProfile: DateProfile;
      nextDayThreshold: Duration;
    }
  ) => {
    return null;
  };

  const summaryViewPlugin = createPlugin({
    views: {
      summaryView: summaryView as ViewComponentType,
      categoryView: categoryView as ViewComponentType
    }
  });

  const handleEventClick = (info: EventClickArg) => {
    const isRecurringEvent =
      info.event.extendedProps.recurringStatus === RecurringStatus.Series ||
      info.event.extendedProps.recurringStatus ===
        RecurringStatus.DetachedSeries;

    const occurrenceDate = isRecurringEvent
      ? parseISO(
          info.event.extendedProps.eventStartDateTimeOffSet!
        ).toISOString()
      : undefined;

    eventsDomainStore.setOccurrenceDate(occurrenceDate);

    loadEventDetail(info.event.id);
    setIsEventDetailModalOpen(true);
  };

  //Also need to refresh data when view is changed
  useEffect(
    () => {
      if (
        calendarApi.currentDataManager !== null &&
        calendarApi.currentDataManager !== undefined
      ) {
        handleViewChange();
      }
      updateCalendarDate();
    },
    // eslint-disable-next-line
    [calendarApi, currentCalendarView]
  );

  function updateCalendarDate() {
    let activeStart =
      isFirstLoad && eventsUiStore.calendarStart
        ? eventsUiStore.calendarStart
        : calendarApi?.currentDataManager?.getCurrentData().viewApi.activeStart;
    let activeEnd =
      isFirstLoad && eventsUiStore.calendarEnd
        ? eventsUiStore.calendarEnd
        : calendarApi?.currentDataManager?.getCurrentData().viewApi.activeEnd;
    if (
      activeStart === null ||
      activeStart === undefined ||
      activeEnd === null ||
      activeEnd === undefined
    ) {
      return;
    }
    loadEvents(activeStart, activeEnd);
  }

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  function isMonthView() {
    return currentCalendarView === calendarViewMonth;
  }

  function isWeekView() {
    return currentCalendarView === calendarViewWeek;
  }

  function calculateMaxEvents() {
    //Weekly has limit of 3 all day events
    return currentCalendarView === calendarViewWeek ? 2 : undefined;
  }

  let clicks = 0;
  let doubleClickTimerId = setTimeout(() => {}, 500);
  let dateTimeClicked = "";
  let shouldCreateEvent = false;
  const reset = () => {
    clicks = 0;
    clearTimeout(doubleClickTimerId);
    dateTimeClicked = "";
    shouldCreateEvent = false;
  };

  const handleDateClick = ({date, dateStr}: DateClickArg) => {
    clicks++;
    if (clicks === 1) {
      dateTimeClicked = dateStr;
      doubleClickTimerId = setTimeout(() => {
        reset();
      }, 500);
    } else {
      if (clicks === 2 && dateTimeClicked === dateStr) {
        let startDate;
        const startDateInUtc = zonedTimeToUtc(date, timeZone);
        switch (calendarApi?.view.type) {
          case calendarViewWeek: {
            if (startDateInUtc.getTime() >= addHours(new Date(), 1).getTime()) {
              shouldCreateEvent = true;
              startDate = startDateInUtc;
            }
            break;
          }
          case calendarViewMonth: {
            if (date.getTime() >= new Date().setHours(0, 0, 0, 0)) {
              shouldCreateEvent = true;
              startDate = date;
              if (new Date().getMinutes() < 30) startDate.setMinutes(30);
              // If the minute is greater than 30, we would need keep the minute as 0 and set hours to 2 hours from now on
              // i.e. when creating an event at 8.59am should set the start time to 10.00 am (8+2)
              startDate.setHours(
                new Date().getHours() + (new Date().getMinutes() < 30 ? 1 : 2)
              );
            }
            break;
          }
        }
        if (shouldCreateEvent) {
          createEventOnClick(startDate);
        }
      }
      reset();
    }
  };

  function getSlotMinTime() {
    const eventStartHours = events.map((e) => e.start.getHours());
    const earliestStart = Math.min(...eventStartHours);
    return earliestStart < 8
      ? `${earliestStart}:00:00`
      : defaultCalendarSlotMinTime;
  }

  function getSlotMaxTime() {
    const eventEndHours = events.map((e) => e.end.getHours());
    const latestEnd = Math.max(...eventEndHours);
    const earliestEndTime = Math.min(...eventEndHours);
    return earliestEndTime === 0 //cater for midnight
      ? "24:00:00"
      : latestEnd > 22
        ? `${latestEnd + 1}:00:00`
        : defaultCalendarSlotMaxTime;
  }

  const now = utcToZonedTime(new Date(), timeZone);
  const getUnreleasedDateClass = (calendarDate: Date, Type?: String) => {
    if (isMultiVillageOptionSelected) {
      return "";
    }

    let className = "";
    const isSameOrAfter = isDateOneSameOrAfterDateTwo(
      calendarDate,
      calendarSchedule?.startDate
    );
    const isFutureUnrelese = isDateOneSameOrAfterDateTwo(
      calendarDate,
      calendarSchedule?.endDate
    );
    const isEqual = checkDatesEqual(calendarDate, calendarSchedule?.startDate);
    if (isWeekView() && Type === "Header") {
      className += isSameOrAfter ? "unreleased-header-background-week " : "";
    } else {
      className += isSameOrAfter ? "unreleased-background " : "";
    }
    if (isMonthView() && !Type && isSameOrAfter) {
      className += "unreleased-background unreleased-cell-background-month ";
    }
    className += isEqual ? "unreleased-border-left " : "";
    if (isFutureUnrelese) className += "future-unrelese";
    return className;
  };

  const getFormattedHeader = (date: Date) => {
    let formatter = new Intl.DateTimeFormat("en", {
      weekday: "short",
      day: "numeric",
      month: "short"
    });

    let dateParts = formatter.formatToParts(date);
    let weekDay = dateParts.find((d) => d.type === "weekday")?.value;
    let dateNumber = dateParts.find((d) => d.type === "day")?.value;
    return {
      weekDay,
      dateNumber
    };
  };

  const findTimeDuration = (
    startDate: Date | null,
    endDate: Date | null
  ): string => {
    if (startDate && endDate) {
      const data = intervalToDuration({
        start: startDate,
        end: endDate
      });
      let duration = "";
      if (data.minutes && !data.hours && !data.days) {
        if (data.minutes <= 15) duration = "event-duration-15";
        if (data.minutes > 15 && data.minutes <= 30)
          duration = "event-duration-30";
        if (data.minutes > 30 && data.minutes <= 45)
          duration = "event-duration-45";
      }
      return duration;
    }
    return "";
  };
  const getFlags = (arg: any) => {
    const releaseDateLocal =
      arg.event.extendedProps.releaseDate &&
      utcToZonedTime(
        arg.event.extendedProps.releaseDate,
        arg.event.extendedProps.timeZone
      );

    const isPastEvent = arg.event.end.getTime() < now.getTime();
    const isVirtualEvent =
      arg.event?.extendedProps?.location ===
      t("event:labels.virtualEventSelect");
    const isBookableEvent =
      !isPastEvent && arg.event.extendedProps.isBookableEvent;
    const isPastBookableEvent =
      isPastEvent && arg.event.extendedProps.isBookableEvent;
    const isFeaturedEvent = arg.event.extendedProps.isFeaturedEvent;
    const isPreReleaseEvent =
      arg.event.extendedProps.isBookableEvent && releaseDateLocal !== undefined;
    const isPreReleaseInPastAndEventInUnreleasedPeriod =
      isPreReleaseEvent &&
      releaseDateLocal < now &&
      arg.event.extendedProps.eventStartDateTimeOffSet >=
        calendarSchedule?.startDate?.toISOString()!;
    const isPreReleaseInPastAndEventInReleasedPeriod =
      isPreReleaseEvent &&
      releaseDateLocal < now &&
      arg.event.extendedProps.eventStartDateTimeOffSet <
        calendarSchedule?.startDate?.toISOString()!;
    const isCancelledOrPostponedEvent =
      EventDetailsHelper.isCancelledOrPostponedStatus(
        arg.event.extendedProps.eventStatus
      );

    return {
      isPastEvent,
      isVirtualEvent,
      isBookableEvent,
      isPastBookableEvent,
      isFeaturedEvent,
      isPreReleaseEvent,
      isPreReleaseInPastAndEventInUnreleasedPeriod,
      isPreReleaseInPastAndEventInReleasedPeriod,
      isCancelledOrPostponedEvent
    };
  };

  const [isUnreleased, setIsUnreleased] = useState(false);

  useEffect(() => {
    /**
     * Check if any time within a timespan is unreleased
     * @param start The start of the timespan
     * @param end The end of the timespan
     * @returns Is any point within the timespan unreleased
     */
    function isTimeSpanUnreleased(
      start?: Date | null,
      end?: Date | null
    ): boolean {
      return (
        !!start &&
        !!end &&
        isDateOneAfterDateTwo(calendarSchedule?.endDate, start) &&
        isDateOneAfterDateTwo(end, calendarSchedule?.startDate)
      );
    }

    const activeStart =
      calendarApi?.currentDataManager?.getCurrentData().viewApi.currentStart;
    const activeEnd =
      calendarApi?.currentDataManager?.getCurrentData().viewApi.currentEnd;
    setIsUnreleased(
      isTimeSpanUnreleased(activeStart, activeEnd) &&
        !isMultiVillageOptionSelected
    );
    document.documentElement.style.setProperty(
      "--sticky-top",
      isUnreleased ? "181px" : "139px"
    );
  }, [
    calendarApi.currentDataManager,
    calendarSchedule?.endDate,
    calendarSchedule?.startDate,
    isMultiVillageOptionSelected,
    isUnreleased
  ]);

  const search = useLocation().search;
  const queryParams = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );
  const selectedCalendarName = decodeURI(
    queryParams.get(QueryParams.calendar) || ""
  );
  useEffect(() => {
    runInAction(() => {
      eventsUiStore.selectedCalendarName = selectedCalendarName;
    });
    updateCalendarDate();
  }, [eventsUiStore, selectedCalendarName]);

  const calendarColor = eventsUiStore.calendarColor;
  const calendarIcon = eventsUiStore.calendarIcon;
  const isCategoryViewSelected =
    eventsUiStore.currentCalendarView === calendarViewCategory;

  useEffect(() => {
    if (isMultiVillageOptionSelected && isCategoryViewSelected) {
      runInAction(() => {
        eventsUiStore.currentCalendarView = eventsUiStore.defaultCalendarView;
      });
    }
  }, [eventsUiStore, isMultiVillageOptionSelected, isCategoryViewSelected]);

  const shouldShowCategoryView =
    isCategoryViewSelected && !isMultiVillageOptionSelected;

  return (
    <>
      <EventDetailModal
        eventDetails={eventDetail}
        isLoading={eventDetailIsLoading}
        toggle={() => setIsEventDetailModalOpen(!isEventDetailModalOpen)}
        eventIsReleased={eventsUiStore.isCurrentEventReleased()}
        isOpen={isEventDetailModalOpen}
        onEditEventClick={onEditEventClick}
        onViewEventClick={onViewEventClick}
        switchToManageBooking={switchToManageBooking}
        isMultiVillageOptionSelected={isMultiVillageOptionSelected}
        switchToEditEventSeries={switchToEditEventSeries}
        switchToViewEventSeries={switchToViewEventSeries}
        currentDateTime={eventsDomainStore.getCurrentTime()}
        isEnhancedCalendarEvent={eventsUiStore.isEnhancedCalendar}
      />
      <Card className="mb-3">
        <div className="sticky-top">
          <PageHeading
            title={t("event:titles.calendar")}
            actionTitle={t("event:labels.calendar.add")}
            actionIcon={IconSource.add}
            onActionClicked={() => createEventOnClick(undefined)}
            haloIcon={calendarIcon}
            haloColor={calendarColor}
          />
          <EventCalendarHeader
            calendarApi={calendarApi}
            updateCalendarDate={updateCalendarDate}
            currentCalendarView={currentCalendarView}
            handleViewChange={setCurrentCalendarView}
            isLoading={isLoading}
            calendarSchedule={calendarSchedule}
            isMultiVillageOptionSelected={isMultiVillageOptionSelected}
          />
        </div>
        <CardBody
          className="p-0"
          style={{
            height: "auto",
            minHeight: "65vh"
          }}
        >
          <div
            className={
              isNavigating || isLoading
                ? `hideCalendarContent-${currentCalendarView}`
                : "calendarContent"
            }
          >
            <div hidden={!shouldShowCategoryView}>
              <EventCategoryAccordion
                eventCategories={eventsDomainStore.categorisedEvents}
                onEventClick={handleEventClick}
                calendarScheduleStartDate={
                  eventsUiStore.calendarSchedule?.startDate
                }
              />
            </div>
            <div hidden={shouldShowCategoryView}>
              <FullCalendar
                now={now}
                ref={calendarRef}
                headerToolbar={false}
                contentHeight="auto"
                firstDay={1}
                plugins={[
                  dayGridPlugin,
                  bootstrapPlugin,
                  timeGridPlugin,
                  interactionPlugin,
                  listPlugin,
                  summaryViewPlugin
                ]}
                initialDate={initialDate}
                dayMaxEvents={calculateMaxEvents()}
                allDaySlot={false}
                scrollTimeReset={false}
                slotMinTime={getSlotMinTime()}
                slotMaxTime={getSlotMaxTime()}
                initialView={defaultCalendarView}
                themeSystem="bootstrap"
                stickyHeaderDates={"auto"}
                height={"auto"}
                // @ts-ignore
                views={views}
                eventTimeFormat={{
                  hour: "numeric",
                  minute: "2-digit",
                  omitZeroMinute: true,
                  meridiem: "narrow"
                }}
                eventDisplay="block"
                dayMaxEventRows={11}
                displayEventEnd={false}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                events={events}
                dayCellContent={(arg) => {
                  const cellDate = arg.date.setHours(0, 0, 0, 0);
                  const now = utcToZonedTime(new Date(), timeZone).setHours(
                    0,
                    0,
                    0,
                    0
                  );
                  const isToday = cellDate === now;
                  const isPast = cellDate < now;
                  return (
                    isMonthView() && (
                      <div
                        className={classNames({
                          "is-today": isToday,
                          "is-past": isPast
                        })}
                      >
                        {arg.dayNumberText}
                      </div>
                    )
                  );
                }}
                eventClassNames={(arg) => {
                  const flags = getFlags(arg);
                  return classNames({
                    "is-past": flags.isPastEvent,
                    "past-event-month":
                      flags.isPastEvent &&
                      isMonthView() &&
                      !flags.isCancelledOrPostponedEvent,
                    "past-event-week": flags.isPastEvent && !isMonthView(),
                    "virtual-event": flags.isVirtualEvent,
                    "bookable-event": flags.isBookableEvent,
                    "past-bookable-event":
                      flags.isPastBookableEvent &&
                      !flags.isCancelledOrPostponedEvent,
                    "non-bookable-event": !flags.isBookableEvent,
                    "no-remaining-capacity":
                      (flags.isPastEvent || flags.isBookableEvent) &&
                      arg.event.extendedProps.isFullyBooked,
                    "featured-event": flags.isFeaturedEvent,
                    "pre-release-event": flags.isPreReleaseEvent,
                    "pre-release-in-past-event-in-unreleased-period":
                      flags.isPreReleaseInPastAndEventInUnreleasedPeriod,
                    "pre-release-in-past-event-in-released-period":
                      flags.isPreReleaseInPastAndEventInReleasedPeriod,
                    "cancelled-postponed-event":
                      flags.isCancelledOrPostponedEvent
                  });
                }}
                eventContent={(arg) => {
                  const duration = findTimeDuration(
                    arg.event.start,
                    arg.event.end
                  );
                  const flags = getFlags(arg);

                  const eventIcon = (
                    <EventIcon
                      isBookableEvent={arg.event.extendedProps.isBookableEvent}
                      isCancelledOrPostponedEvent={
                        flags.isCancelledOrPostponedEvent
                      }
                      isFeaturedEvent={flags.isFeaturedEvent}
                      isPreReleaseEvent={flags.isPreReleaseEvent}
                      isPreReleaseInPastAndEventInUnreleasedPeriod={
                        flags.isPreReleaseInPastAndEventInUnreleasedPeriod
                      }
                      isPreReleaseInPastAndEventInReleasedPeriod={
                        flags.isPreReleaseInPastAndEventInReleasedPeriod
                      }
                      isPastEvent={flags.isPastEvent}
                      isVirtualEvent={flags.isVirtualEvent}
                      recurringStatus={arg.event.extendedProps.recurringStatus}
                    />
                  );

                  return (
                    <div id={`event-${arg.event.id}`}>
                      <div
                        className={classNames(`event-title ${duration}`, {
                          "is-past": flags.isPastEvent
                        })}
                      >
                        <div
                          className={classNames("event-title-card", {
                            "virtual-event": flags.isVirtualEvent
                          })}
                        >
                          <span className="title">
                            {arg.event.title}
                            {isMonthView() && eventIcon}
                          </span>
                          {duration !== "event-duration-15" &&
                            isWeekView() &&
                            eventsUiStore.isEnhancedCalendar && (
                              <span className="event-location">
                                {arg.event.extendedProps?.location}
                              </span>
                            )}
                        </div>
                        {isWeekView() && eventIcon}
                      </div>
                      <UncontrolledTooltip
                        modifiers={{
                          preventOverflow: {boundariesElement: "window"}
                        }}
                        fade={false}
                        className="event-tooltip"
                        target={`event-${arg.event.id}`}
                      >
                        {arg.event.title}
                      </UncontrolledTooltip>
                    </div>
                  );
                }}
                dayHeaderContent={(arg) => (
                  <div
                    className={
                      "eventDateHeading line-height-1 row no-gutters justify-content-center align-items-center " +
                      classNames({"unreleased-heading": isUnreleased})
                    }
                  >
                    {isWeekView() && (
                      <div className="event-day-number">
                        <div
                          className={classNames({
                            "is-today-day-number": isToday(arg.date),
                            "is-past": arg.isPast
                          })}
                        >
                          {getFormattedHeader(arg.date).dateNumber}
                        </div>
                      </div>
                    )}
                    <div
                      className={classNames("pl-2", {
                        "is-today-week": isToday(arg.date),
                        "is-past": arg.isPast
                      })}
                    >
                      {getFormattedHeader(arg.date).weekDay}
                    </div>
                  </div>
                )}
                dayHeaderClassNames={(arg) =>
                  classNames({
                    [getUnreleasedDateClass(arg.date, "Header")]:
                      !isMonthView(),
                    "unreleased-heading": isUnreleased
                  })
                }
                dayCellClassNames={(arg) => getUnreleasedDateClass(arg.date)}
                nowIndicator={true}
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default observer(EventsCalendarTemplate);
